import { TailoringSkeleton } from '@/components/elements/_skeletons/tailoring.skeleton';
import { BuildingBlockData } from '@/components/elements/building-blocks/building-block-data/building-block-data.component';
import { BuildingBlockTitle } from '@/components/elements/building-blocks/building-block-title/building-block-title.component';
import { BuildingBlocks } from '@/components/elements/building-blocks/building-blocks.component';
import { BuildingBlocksHeading } from '@/components/elements/building-blocks/building-blocks.heading';
import { Topbar } from '@/components/elements/topbar/topbar.component';
import { T } from '@/components/i18n/message.i18n.component';
import { TailoringLayout } from '@/components/layout/tailoring/tailoring.layout.component';
import { DesignObligation, ProductBuildingBlock, Template, TemplatePublicationStatus } from '@/gql/graphql';
import { useBuildingBlocks } from '@/hooks/use-building-blocks/use-building-blocks.hook';
import { GroupedBuildingBlock } from '@/hooks/use-building-blocks/use-building-blocks.hook.core';
import { useQuestions } from '@/hooks/use-questions/use-questions.hook';
import { useToast } from '@/hooks/use-toast.hook';
import { useUser } from '@/hooks/use-user/use-user.hook';
import { isTemplateTailoringEnabled } from '@/lib/template.utils';
import { tailorFormsTranslations } from '@/pages/products/tailoring/tailoring-forms.messages';
import { tailoringMessages } from '@/pages/products/tailoring/tailoring.messages';
import {
  getFirstBBContext,
  inclusionBBSelection,
  isSelectedBBIncluded,
} from '@/pages/products/tailoring/tailoring.page.core';
import { useDialogStore } from '@/store/dialog.store';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { allTemplates, getTemplate, updateTemplate } from '../templates.requests';
import { TemplateTailorBuildingBlockForm } from './forms/template-tailor-bb.form';
import { TemplateTailorDesignObligationForm } from './forms/template-tailor-do.form';
import { templateTailoringTopbarConfig } from './templates-tailoring.page.config';

export const TemplatesTailoringPage = () => {
  const intl = useIntl();

  const { guid } = useUser();
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [bbTab, setBBTab] = useState<'general-details' | 'information' | 'data-blocks' | 'design-obligation'>(
    'general-details',
  );

  const { openDialog } = useDialogStore((state) => state);

  const { data, loading } = useQuery<{ template: Template }>(getTemplate, {
    variables: { id: id || '' },
  });

  const [updateTemplateMutation] = useMutation(updateTemplate);

  const { progress } = useQuestions({
    tailoringProcess: data?.template?.tailoringProcess,
    id: data?.template?.id,
  });

  const {
    selectedBuildingBlock: selectedBB,
    includedBuildingBlocks,
    excludedBuildingBlocks,
    sort,
    setSelectedBuildingBlock: setSelectedBB,
    handleSortChange: onSortChange,
    selectedTab,
    setSelectedTab,
  } = useBuildingBlocks({
    buildingBlocks: data?.template.tailoringProcess.buildingBlocks,
  });

  const handleExport = async () => {
    if (!data?.template) return;
    await updateTemplateMutation({
      variables: {
        updateTemplateInput: {
          id: id || '',
          name: data?.template.name,
          description: data?.template.description,
          publicationStatus: TemplatePublicationStatus.Published,
        },
      },
      refetchQueries: [{ query: allTemplates }],
    });
    navigate('/templates');
    toast({
      description: intl.formatMessage({
        id: 'templateTailoring.page.templatePublished',
        defaultMessage: 'Template successfully published.',
        description: 'Template published toast',
      }),
      variant: 'default',
    });
  };

  const handleBlockClick = (bb: ProductBuildingBlock) => {
    setSelectedBB(bb);
  };

  // select first BB if none is selected when the page loads
  useEffect(() => {
    if (selectedBB) return;
    const selected = getFirstBBContext(selectedTab, includedBuildingBlocks, excludedBuildingBlocks);
    if (!selected) return;
    setSelectedBB(selected);
  }, [includedBuildingBlocks, excludedBuildingBlocks]);

  const handleTabChange = (context: 'included' | 'excluded') => {
    setSelectedTab(context);
    if (context === 'included' && includedBuildingBlocks.data.length > 0) {
      setSelectedBB(includedBuildingBlocks.data[0].buildingBlocks[0]);
    } else if (context === 'excluded' && excludedBuildingBlocks.data.length > 0) {
      setSelectedBB(excludedBuildingBlocks.data[0].buildingBlocks[0]);
    }
  };

  const onBBInclusion = (bb: ProductBuildingBlock) => {
    openDialog({
      type: 'custom',
      title: isSelectedBBIncluded(bb) ? (
        <T {...tailorFormsTranslations.bbTailroingFormTitleExclude} />
      ) : (
        <T {...tailorFormsTranslations.bbTailroingFormTitleInclude} />
      ),

      element: (
        <TemplateTailorBuildingBlockForm
          templateId={data?.template.id}
          bb={bb}
          afterAdd={(payload) => {
            const currentBuildingBlocks = isSelectedBBIncluded(bb) ? includedBuildingBlocks : excludedBuildingBlocks;
            const includedBB = currentBuildingBlocks.data.find((group) => {
              return group.buildingBlocks.find((b) => b.id === bb.id);
            });
            if (!includedBB) return;
            setSelectedBB(inclusionBBSelection(includedBB, [payload], currentBuildingBlocks));
          }}
        />
      ),
    });
  };

  const handleIncludeExcludeDO = (groupedBB: GroupedBuildingBlock<DesignObligation>, including: boolean) => {
    const translation = including
      ? tailoringMessages.includeDesignObligation
      : tailoringMessages.excludeDesignObligation;

    openDialog({
      type: 'custom',
      fullScreen: true,
      title: <T {...translation} />,
      element: (
        <TemplateTailorDesignObligationForm
          templateId={data?.template.id || ''}
          includedBB={including}
          groupedBB={groupedBB}
          afterAdd={(bbs) => {
            if (!setSelectedBB) return;
            if (including) {
              setSelectedBB(inclusionBBSelection(groupedBB, bbs, excludedBuildingBlocks));
            } else {
              setSelectedBB(inclusionBBSelection(groupedBB, bbs, includedBuildingBlocks));
            }
          }}
        />
      ),
    });
  };

  return (
    <TailoringLayout
      loading={loading}
      loadingElement={<TailoringSkeleton />}
      topbar={
        <Topbar
          config={templateTailoringTopbarConfig(
            navigate,
            progress,
            id || '',
            !isTemplateTailoringEnabled(guid, data?.template),
            handleExport,
          )}
        />
      }
      leftAreaFixed={
        <BuildingBlockTitle
          bbTab={bbTab}
          setBBTab={setBBTab}
          bb={selectedBB}
          handleBBInclusion={onBBInclusion}
          tailoringEnabled={isTemplateTailoringEnabled(guid, data?.template)}
        />
      }
      leftArea={<BuildingBlockData bb={selectedBB} bbTab={bbTab} />}
      rightAreaFixed={
        <BuildingBlocksHeading
          sort={sort}
          onTabChange={handleTabChange}
          handleSortChange={onSortChange}
          includedBuildingBlocks={includedBuildingBlocks}
          excludedBuildingBlocks={excludedBuildingBlocks}
        />
      }
      rightArea={
        <BuildingBlocks
          sort={sort}
          enableInclusion={isTemplateTailoringEnabled(guid, data?.template)}
          selectedTab={selectedTab}
          onBlockClick={handleBlockClick}
          selectedBuildingBlockId={selectedBB?.id}
          includedBuildingBlocks={includedBuildingBlocks}
          excludedBuildingBlocks={excludedBuildingBlocks}
          onBBInclusionChange={handleIncludeExcludeDO}
        />
      }
    />
  );
};
